import { Button, Dimmer, Form, Grid, Icon, Segment } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';
import Selector from '@/components/Autre/Selector/Selector';
import { MODES_REGLEMENTS } from '@/constants';
import { ModeReglementContext } from '@/contexts';
import type {
  BonCAFType,
  BonCCASType,
  CarteBancaireType,
  CesuType,
  ChequeType,
  ChequeVacancesType,
  DataReglementType,
  NumeraireType,
  TicketType,
  VirementType,
} from '@/types';

import {
  BonCAF,
  BonCCAS,
  CarteBancaire,
  Cesu,
  Cheque,
  ChequeVacances,
  Numeraire,
  Tickets,
  Virement,
} from '../../Reglement';
import ConfirmationChangement from '../ConfirmationChangement';

interface ModalEncaissementReglementProps {
  readOnly?: boolean;
  indice: number;
  payeur?: string;
  reste?: number;
  reglement: DataReglementType;
  onReglementChange?: (data: DataReglementType | null, complet: boolean) => void;
  onReglementReset?: (identifiantModeReglement: number) => void;
  onReglementMultipleDelete?: (indice: number) => void;
  buttonAddReglement?: React.ReactNode | null;
  reglementsDesactives?: string[];
}

const ModalEncaissementReglement = (props: ModalEncaissementReglementProps) => {
  const {
    readOnly = false,
    indice,
    payeur,
    reste,
    reglement,
    onReglementChange,
    onReglementReset,
    onReglementMultipleDelete,
    buttonAddReglement,
    reglementsDesactives = [],
  } = props;

  const [dimmed, setDimmed] = React.useState<boolean>(false);
  const [tmpModeReglement, setTmpModeReglement] = React.useState<number | null>();

  const { regieModesReglement } = React.useContext(ModeReglementContext);

  const { identifiantModeReglement } = reglement;

  // On retire PRE, CBI et PRI quoi qu'il arrive pendant l'encaissement
  const filteredModesReglements = React.useMemo(
    () =>
      _.filter(
        regieModesReglement,
        ({ code }) =>
          code !== MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET &&
          code !== MODES_REGLEMENTS.PRELEVEMENT_INTERNET &&
          code !== MODES_REGLEMENTS.PRELEVEMENT,
      ),
    [regieModesReglement],
  );

  // Options pour le Selector avec les modes désactivés marqués
  const optionsModesReglements = React.useMemo(
    () =>
      _.map(filteredModesReglements, (modeReglement) => ({
        key: `modeReglement@${modeReglement.identifiant}`,
        value: modeReglement.identifiant,
        text: _.upperFirst(modeReglement.libelle),
        disabled: _.includes(reglementsDesactives, modeReglement.code),
      })),
    [filteredModesReglements, reglementsDesactives],
  );

  const paiement = React.useMemo(
    () => _.find(filteredModesReglements, { identifiant: identifiantModeReglement }),
    [filteredModesReglements, identifiantModeReglement],
  );

  const handleConfirmCancel = () => {
    setDimmed(false);
  };

  const handleConfirmValidate = () => {
    setDimmed(false);
    setTmpModeReglement(null);
    onReglementReset(tmpModeReglement);
  };

  const handleModeReglementSelect = (e, { value }: { value: number }) => {
    if (!_.isEmpty(_.omit(reglement, 'identifiantModeReglement', 'identifiant', 'complet'))) {
      setDimmed(true);
      setTmpModeReglement(value);
    } else {
      onReglementReset(value);
    }
  };

  return (
    <Dimmer.Dimmable dimmed={dimmed} blurring>
      <Segment basic style={{ paddingRight: 0, marginBottom: 0 }}>
        <Grid as={Form} style={{ width: '100%' }}>
          {!readOnly && (
            <Grid.Row columns={1} style={{ paddingBottom: 0 }}>
              <Grid.Column
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingRight: 0,
                  alignItems: 'center',
                }}
              >
                <Selector
                  name={`modeReglement@${indice}`}
                  value={identifiantModeReglement}
                  options={optionsModesReglements}
                  onChange={handleModeReglementSelect}
                />
                {buttonAddReglement}
              </Grid.Column>
            </Grid.Row>
          )}
          <Grid.Row>
            {readOnly && (
              <Grid.Column width={2}>
                <Form.Group
                  style={{
                    alignItems: 'center',
                    height: '100%',
                    margin: 0,
                  }}
                >
                  <Form.Field>
                    <label>R{indice + 1}</label>
                  </Form.Field>
                  {/* @ts-ignore  */}
                  <Form.Field style={{ flex: 1, textAlign: 'center' }}>
                    <label>{paiement?.libelle}</label>
                  </Form.Field>
                </Form.Group>
              </Grid.Column>
            )}
            <Grid.Column style={{ paddingRight: 0 }} width={readOnly ? 14 : 16}>
              {onReglementMultipleDelete && (
                <Button
                  data-testid="button-reglement-delete"
                  size="small"
                  basic
                  negative
                  floated="right"
                  onClick={() => onReglementMultipleDelete(indice)}
                  style={{
                    width: 205,
                  }}
                  type="button"
                >
                  <Icon name="trash" />
                  Supprimer ce règlement
                </Button>
              )}
              {paiement?.code === MODES_REGLEMENTS.CHEQUE && (
                <Cheque
                  disabled={readOnly}
                  payeur={payeur}
                  onChange={onReglementChange as (cheque: ChequeType) => void}
                  reste={reste}
                  {...(reglement as ChequeType)}
                />
              )}
              {paiement?.code === MODES_REGLEMENTS.NUMERAIRE && (
                <Numeraire
                  disabled={readOnly}
                  onChange={onReglementChange as (numeraire: NumeraireType) => void}
                  reste={_.min([reste, 300])}
                  {...(reglement as NumeraireType)}
                />
              )}
              {paiement?.code === MODES_REGLEMENTS.CARTE_BANCAIRE && (
                <CarteBancaire
                  disabled={readOnly}
                  onChange={onReglementChange as (carteBancaire: CarteBancaireType) => void}
                  reste={reste}
                  {...(reglement as CarteBancaireType)}
                />
              )}
              {paiement?.code === MODES_REGLEMENTS.CESU && (
                <Cesu
                  disabled={readOnly}
                  onChange={onReglementChange as (cesu: CesuType) => void}
                  reste={reste}
                  {...(reglement as CesuType)}
                />
              )}
              {paiement?.code === MODES_REGLEMENTS.CHEQUE_VAC && (
                <ChequeVacances
                  disabled={readOnly}
                  onChange={onReglementChange as (chequeVacance: ChequeVacancesType) => void}
                  reste={reste}
                  {...(reglement as ChequeVacancesType)}
                />
              )}
              {paiement?.code === MODES_REGLEMENTS.BON_CCAS && (
                <BonCCAS
                  disabled={readOnly}
                  onChange={onReglementChange as (bonCcas: BonCCASType) => void}
                  reste={reste}
                  {...(reglement as BonCCASType)}
                />
              )}
              {paiement?.code === MODES_REGLEMENTS.BON_CAF && (
                <BonCAF
                  disabled={readOnly}
                  onChange={onReglementChange as (bonCaf: BonCAFType) => void}
                  reste={reste}
                  {...(reglement as BonCAFType)}
                />
              )}
              {paiement?.code === MODES_REGLEMENTS.TICKET && (
                <Tickets
                  disabled={readOnly}
                  onChange={onReglementChange as (ticket: TicketType) => void}
                  reste={reste}
                  {...(reglement as TicketType)}
                />
              )}
              {paiement?.code === MODES_REGLEMENTS.VIREMENT && (
                <Virement
                  disabled={readOnly}
                  payeur={payeur}
                  onChange={onReglementChange as (virement: VirementType) => void}
                  reste={reste}
                  {...(reglement as VirementType)}
                />
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>

      <Dimmer inverted active={dimmed}>
        {dimmed && (
          <ConfirmationChangement
            libelle={
              _.find(filteredModesReglements, { identifiant: identifiantModeReglement })?.libelle
            }
            onValidate={handleConfirmValidate}
            onCancel={handleConfirmCancel}
          />
        )}
      </Dimmer>
    </Dimmer.Dimmable>
  );
};

export default ModalEncaissementReglement;
